import { useGetChatQuery } from "../queries/get-chat";
import { useChat as useSdkChat } from "@ai-sdk/react";
import { config } from "../../lib/config";
import React, { useEffect } from "react";
import { getDefaultApiClientHeaders } from "../service/tekkr-service";
import { Attachment, Message } from "ai";
import { createTekkrFileUrl } from "tekkr-common/dist/model/attachments";
import { FileUpload } from "../../components/shared/chat/chat-input-box";

type SdkChatWrapper = Pick<ReturnType<typeof useSdkChat>, "messages" | "status"> & {
    sendMessage: (message: string, attachments?: Attachment[]) => Promise<void>;
    appendAssistantMessage: (content: string) => Promise<void>;
    error: Error | undefined;
    reload: () => void;
};

export function useSdkChatWrapper(
    chatId: string,
    headerCredentials: Record<string, string>,
    options?: {
        onMessageReceived?: (message: Message) => void;
        onError?: (error: Error) => void;
    }
): SdkChatWrapper {
    const existingMessages = useGetChatQuery(chatId);
    const sdkChat = useSdkChat({
        id: chatId,
        initialMessages: existingMessages.data?.messages || [],
        api: `${config.serviceHost}/api/chats/${chatId}`,
        headers: headerCredentials,
        onError: (error) => {
            options?.onError?.(error);
        },
        onFinish: (message) => options?.onMessageReceived?.(message),
    });

    return {
        messages: sdkChat.messages,
        status: sdkChat.status,
        error: sdkChat.error,
        reload: sdkChat.reload,
        sendMessage: async (message, attachments) => {
            await sdkChat.append({
                role: "user",
                content: message,
                experimental_attachments: attachments,
            });
        },
        appendAssistantMessage: async (content: string) => {
            await sdkChat.append({
                role: "assistant",
                content,
            });
        },
    };
}

export function useChatCredentials() {
    const [credentialHeaders, setCredentialHeaders] = React.useState<Record<string, string> | null>(null);
    useEffect(() => {
        if (!credentialHeaders) {
            getDefaultApiClientHeaders().then(setCredentialHeaders);
        }
    }, [credentialHeaders]);
    return credentialHeaders;
}

export const GOOGLE_DRIVE_CONTENT_TYPE = "text/x-google-drive";

export function mapUploadsToAttachments(uploads: FileUpload[]): Attachment[] {
    return uploads.map((u) => ({
        name: u.name,
        contentType: u.source === "google-drive" ? GOOGLE_DRIVE_CONTENT_TYPE : u.mimeType,
        url: createTekkrFileUrl(u.id),
    }));
}
